import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type {
  JobWageDetail,
  JobWageRateRow,
  JobWageSummary,
  WageClassificationRow,
} from '@/api/types'

const BLANK = {
  wage_classification_id: 0,
  reg_rate: '',
  ot_rate: '',
  dt_rate: '',
  fringe: '0',
  effective_from: '2000-01-01',
  effective_to: '',
  notes: '',
}

/**
 * Prevailing wage rates by job and classification.
 *
 * The job card holds one rate, but only 28 of 82 prevailing jobs actually pay
 * a single rate — 26 pay two, and nine pay six or more. A determination is a
 * rate per classification, so that is what this edits.
 *
 * What Sage has actually paid on each job is shown alongside, because entering
 * a determination blind is how the wrong number ends up in the table. If the
 * observed rates and the entered rates disagree, one of them is wrong and it is
 * worth knowing which before payroll runs.
 */
export function WageRatesPage() {
  const [jobs, setJobs] = useState<JobWageSummary[] | null>(null)
  const [classes, setClasses] = useState<WageClassificationRow[]>([])
  const [open, setOpen] = useState<JobWageDetail | null>(null)
  const [missingOnly, setMissingOnly] = useState(false)
  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [j, c] = await Promise.all([
        api.wageJobs(missingOnly),
        api.wageClassifications(),
      ])
      setJobs(j)
      setClasses(c)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load wage rates')
    }
  }, [missingOnly])

  useEffect(() => {
    void load()
  }, [load])

  async function openJob(jobNo: string) {
    setError(null)
    setEditing(null)
    try {
      setOpen(await api.wageJob(jobNo))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that job')
    }
  }

  const stats = useMemo(() => {
    const all = jobs ?? []
    return {
      total: all.length,
      noRates: all.filter((j) => j.rate_count === 0).length,
      incomplete: all.filter((j) => j.distinct_rates_paid > j.rate_count).length,
    }
  }, [jobs])

  function startNew() {
    setForm({ ...BLANK, wage_classification_id: classes[0]?.wage_classification_id ?? 0 })
    setEditing('new')
    setError(null)
  }

  function startEdit(r: JobWageRateRow) {
    setForm({
      wage_classification_id: r.wage_classification_id,
      reg_rate: String(r.reg_rate),
      ot_rate: r.ot_rate == null ? '' : String(r.ot_rate),
      dt_rate: r.dt_rate == null ? '' : String(r.dt_rate),
      fringe: String(r.fringe),
      effective_from: r.effective_from,
      effective_to: r.effective_to ?? '',
      notes: r.notes ?? '',
    })
    setEditing(r.job_wage_rate_id)
    setError(null)
  }

  async function save() {
    if (!open) return
    setBusy(true)
    setError(null)
    try {
      const payload = {
        wage_classification_id: form.wage_classification_id,
        reg_rate: Number(form.reg_rate),
        ot_rate: form.ot_rate === '' ? null : Number(form.ot_rate),
        dt_rate: form.dt_rate === '' ? null : Number(form.dt_rate),
        fringe: Number(form.fringe || 0),
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
        notes: form.notes || null,
      }
      if (editing === 'new') await api.addWageRate(open.job_no, payload)
      else if (typeof editing === 'number') await api.updateWageRate(editing, payload)
      setEditing(null)
      setOpen(await api.wageJob(open.job_no))
      await load()
      setNotice('Rate saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function remove(r: JobWageRateRow) {
    if (!open) return
    if (!confirm(`Delete the ${r.classification_description} rate of ${r.reg_rate}?`)) return
    setBusy(true)
    setError(null)
    try {
      await api.deleteWageRate(r.job_wage_rate_id)
      setOpen(await api.wageJob(open.job_no))
      await load()
      setNotice('Rate deleted.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete')
    } finally {
      setBusy(false)
    }
  }

  /** Pre-fill from a rate Sage has actually paid — faster and less error-prone
   *  than retyping a figure that is already on screen. */
  function useObserved(rate: number) {
    setForm({
      ...BLANK,
      wage_classification_id: classes[0]?.wage_classification_id ?? 0,
      reg_rate: String(rate),
    })
    setEditing('new')
  }

  // ------------------------------------------------------------------ detail

  if (open) {
    return (
      <div className="stack">
        {error && <div className="alert alert-error">{error}</div>}
        {notice && <div className="alert alert-info">{notice}</div>}

        <div className="card">
          <div className="crew-heading">
            <h2 className="title" style={{ margin: 0 }}>
              <span className="mono">{open.job_no}</span> {open.job_description}{' '}
              {open.is_prevailing_wage && (
                <span className="badge badge-warn">Prevailing wage</span>
              )}
              {open.is_davis_bacon && (
                <span className="badge badge-warn">Davis-Bacon</span>
              )}
              {open.is_certified && <span className="badge badge-info">Certified</span>}
            </h2>
            <button className="btn btn-secondary" onClick={() => { setOpen(null); setEditing(null) }}>
              ← All jobs
            </button>
          </div>
          <p className="muted">
            Job card rate:{' '}
            {open.card_reg_wage ? (
              <strong>{Number(open.card_reg_wage).toFixed(2)}</strong>
            ) : (
              <span className="badge badge-warn">not set</span>
            )}
            {open.card_fringe ? ` + ${Number(open.card_fringe).toFixed(2)} fringe` : ''}
            {' · '}The rates below take precedence over it.
          </p>
        </div>

        <div className="card">
          <div className="crew-heading">
            <h2 className="title" style={{ margin: 0 }}>Rates by classification</h2>
            {editing === null && (
              <button className="btn" onClick={startNew}>+ Add rate</button>
            )}
          </div>

          {open.rates.length === 0 && editing === null ? (
            <div className="alert alert-warn">
              No rates set. Time on this job will fall back to the job card, or
              to the employee card if that is blank too.
            </div>
          ) : (
            <div className="table-wrap table-dense">
              <table>
                <thead>
                  <tr>
                    <th>Classification</th>
                    <th className="num">Regular</th>
                    <th className="num">Overtime</th>
                    <th className="num">Double time</th>
                    <th className="num">Fringe</th>
                    <th>Effective from</th>
                    <th>To</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {open.rates.map((r) => (
                    <tr key={r.job_wage_rate_id}>
                      <td><strong>{r.classification_description}</strong></td>
                      <td className="num">{Number(r.reg_rate).toFixed(2)}</td>
                      <td className="num">
                        {r.ot_rate == null ? <span className="muted">1.5×</span> : Number(r.ot_rate).toFixed(2)}
                      </td>
                      <td className="num">
                        {r.dt_rate == null ? <span className="muted">2×</span> : Number(r.dt_rate).toFixed(2)}
                      </td>
                      <td className="num">{Number(r.fringe).toFixed(2)}</td>
                      <td>{r.effective_from}</td>
                      <td>{r.effective_to ?? <span className="muted">current</span>}</td>
                      <td className="muted">{r.notes ?? '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="btn btn-secondary"
                            style={{ minHeight: 30 }}
                            onClick={() => startEdit(r)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ minHeight: 30 }}
                            disabled={busy}
                            onClick={() => void remove(r)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editing !== null && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--c-border)' }}>
              <h2 className="title">{editing === 'new' ? 'New rate' : 'Edit rate'}</h2>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div className="form-field" style={{ minWidth: 200 }}>
                  <label htmlFor="cls">Classification</label>
                  <select
                    id="cls"
                    value={form.wage_classification_id}
                    onChange={(e) =>
                      setForm({ ...form, wage_classification_id: Number(e.target.value) })
                    }
                  >
                    {classes.map((c) => (
                      <option key={c.wage_classification_id} value={c.wage_classification_id}>
                        {c.description}
                        {c.employee_count ? ` (${c.employee_count})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field" style={{ width: 120 }}>
                  <label htmlFor="reg">Regular</label>
                  <input id="reg" type="number" step="0.01" min="0" value={form.reg_rate}
                    onChange={(e) => setForm({ ...form, reg_rate: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 120 }}>
                  <label htmlFor="ot">Overtime</label>
                  <input id="ot" type="number" step="0.01" min="0" value={form.ot_rate}
                    placeholder="1.5×"
                    onChange={(e) => setForm({ ...form, ot_rate: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 120 }}>
                  <label htmlFor="dt">Double time</label>
                  <input id="dt" type="number" step="0.01" min="0" value={form.dt_rate}
                    placeholder="2×"
                    onChange={(e) => setForm({ ...form, dt_rate: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 120 }}>
                  <label htmlFor="fr">Fringe</label>
                  <input id="fr" type="number" step="0.01" min="0" value={form.fringe}
                    onChange={(e) => setForm({ ...form, fringe: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 160 }}>
                  <label htmlFor="from">Effective from</label>
                  <input id="from" type="date" value={form.effective_from}
                    onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 160 }}>
                  <label htmlFor="to">To (optional)</label>
                  <input id="to" type="date" value={form.effective_to}
                    onChange={(e) => setForm({ ...form, effective_to: e.target.value })} />
                </div>
                <div className="form-field" style={{ flex: 1, minWidth: 200 }}>
                  <label htmlFor="notes">Notes</label>
                  <input id="notes" value={form.notes} maxLength={200}
                    placeholder="e.g. WA L&I determination 2026-01"
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
              <p className="muted" style={{ fontSize: '0.8125rem' }}>
                Leave overtime and double time blank to derive them from the
                regular rate. Enter them when the determination states figures
                that are not simple multiples — assuming 1.5× when it is not is
                how a wrong paycheque happens.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn" disabled={busy || !form.reg_rate} onClick={() => void save()}>
                  {busy ? 'Saving…' : 'Save rate'}
                </button>
                <button className="btn btn-secondary" onClick={() => setEditing(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {open.observed.length > 0 && (
          <div className="card">
            <h2>What Sage has actually paid on this job</h2>
            <p className="muted">
              From Time Track history. Use it to check a determination before
              entering it — if these disagree with the rates above, one of them
              is wrong.
            </p>
            <div className="table-wrap table-dense">
              <table>
                <thead>
                  <tr>
                    <th>Earnings code</th>
                    <th className="num">Rate paid</th>
                    <th>Sage labor code</th>
                    <th className="num">Lines</th>
                    <th className="num">People</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {open.observed.map((o, i) => (
                    <tr key={i}>
                      <td className="mono">{o.earnings_code}</td>
                      <td className="num"><strong>{Number(o.pay_rate).toFixed(2)}</strong></td>
                      <td className="muted">{o.labor_code_description ?? o.labor_code ?? '—'}</td>
                      <td className="num">{o.lines}</td>
                      <td className="num">{o.people}</td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ minHeight: 30 }}
                          onClick={() => useObserved(o.pay_rate)}
                        >
                          Use this rate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  // -------------------------------------------------------------------- list

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2>Prevailing wage rates</h2>
        <p className="muted">
          A determination sets a rate per <strong>classification</strong>, not
          one rate per job. Sage's job card holds a single figure, which is why
          most of these jobs need rates here instead.
        </p>
        {jobs && (
          <p>
            <strong>{stats.total}</strong> open prevailing jobs ·{' '}
            <strong>{stats.noRates}</strong> with no rates set ·{' '}
            <strong>{stats.incomplete}</strong> where Sage has paid more
            distinct rates than are configured
          </p>
        )}
        <label>
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
            style={{ minHeight: 'auto', marginRight: 8 }}
          />
          Only jobs with no rates set
        </label>
      </div>

      <div className="card">
        {jobs === null ? (
          <p className="muted">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="muted">No jobs match.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Description</th>
                  <th>Flags</th>
                  <th className="num">Rates set</th>
                  <th className="num">Rates paid</th>
                  <th className="num">Hours</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const gap = j.distinct_rates_paid > j.rate_count
                  return (
                    <tr key={j.job_no}>
                      <td className="mono">{j.job_no}</td>
                      <td>{j.job_description}</td>
                      <td>
                        {j.is_prevailing_wage && (
                          <span className="badge badge-warn">Prevailing</span>
                        )}{' '}
                        {j.is_davis_bacon && (
                          <span className="badge badge-warn">Davis-Bacon</span>
                        )}{' '}
                        {j.is_certified && <span className="badge badge-info">Certified</span>}
                      </td>
                      <td className="num">
                        {j.rate_count === 0 ? (
                          <span className="badge badge-error">none</span>
                        ) : (
                          j.rate_count
                        )}
                      </td>
                      <td className="num">
                        {gap ? (
                          <span className="badge badge-warn">{j.distinct_rates_paid}</span>
                        ) : (
                          j.distinct_rates_paid || <span className="muted">—</span>
                        )}
                      </td>
                      <td className="num">{Number(j.recorded_hours).toFixed(0)}</td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ minHeight: 30 }}
                          onClick={() => void openJob(j.job_no)}
                        >
                          {j.rate_count === 0 ? 'Set rates' : 'Edit rates'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
